import React, { forwardRef } from 'react'
import cx from 'classnames'

import { AdoptionComponent } from '../../constants/AdoptionComponent'
import { useDsAdoptionAttribute } from '../../utils/dsAdoption'
import * as l from '../../utils/localization/format'
import BtnV2 from '../BtnV2'
import LinkBtn from '../BtnV2/LinkBtn'

import { AccountSelect } from './AccountSelect/AccountSelect'
import { MainNavigationProps } from './interfaces'
import MainNavigationItem from './MainNavigationItem'
import { MainNavigationLogo } from './MainNavigationLogo'

import cm from './MainNavigation.module.css'

const MainNavigationComponent = forwardRef<HTMLElement, MainNavigationProps>(
  (
    {
      id,
      expanded = true,
      isLoading,
      ariaLabel,
      skipToContentTarget,
      skipToContentLabel,
      menuContent,
      bottomContent,
      onClick,
      className,
      'data-test-id': dataTestId,
      accounts,
      selectedAccount: selectedAccountId,
      onAccountChange,
      onAccountPinToggle,
      onAccountAddClick,
      onSelectAccountClick,
    }: MainNavigationProps,
    ref: React.Ref<HTMLElement>,
  ) => {
    const mainNavigationClasses = cx(
      cm.mainNavigation,
      {
        [cm.expanded]: expanded,
        [cm.collapsed]: !expanded,
      },
      className,
    )
    const adoptionAttribute = useDsAdoptionAttribute(AdoptionComponent.MAIN_NAVIGATION)

    return (
      <nav
        ref={ref}
        id={id}
        aria-label={ariaLabel}
        className={mainNavigationClasses}
        aria-expanded={expanded}
        data-test-id={dataTestId}
        {...adoptionAttribute}
      >
        <LinkBtn
          href={skipToContentTarget}
          className={cm.skipToContent}
          variant="primary"
          label={skipToContentLabel}
          tabIndex={1}
          fullWidth
        />

        <div className={cm.mainNavigation__top}>
          <div
            className={cx(cm.mainNavigation__logo, expanded && cm.mainNavigation__logo_expanded)}
          >
            <MainNavigationLogo isExpanded={expanded} />
            {expanded && (
              <BtnV2
                variant="ghost-light"
                className={cm.mainNavigation__close}
                ariaLabel={l.translate('Close')}
                onClick={onClick}
                onlyIcon="Close"
              />
            )}
          </div>
          <div className={cx(cm.mainNavigation__slot, cm.accountSelect)}>
            <AccountSelect
              accounts={accounts}
              selectedAccount={selectedAccountId}
              onAccountChange={onAccountChange}
              onAccountPinToggle={onAccountPinToggle}
              onAccountAddClick={onAccountAddClick}
              onSelectAccountClick={onSelectAccountClick}
              isCollapsed={!expanded}
              isLoading={isLoading}
            />
          </div>
          <div className={cm.mainNavigation__menu}>
            <div className={cm.mainNavigation__slot}>{menuContent}</div>

            <BtnV2
              onlyIcon={expanded ? 'NavCollapse' : 'NavExpand'}
              size="default"
              variant="ghost-light"
              className={cm.mainNavigation__toggle}
              ariaLabel={
                expanded ? l.translate('Collapse navigation') : l.translate('Expand navigation')
              }
              onClick={onClick}
            />
          </div>
        </div>
        <div className={cm.mainNavigation__bottom}>
          <div className={cm.mainNavigation__slot_bottom}>{bottomContent}</div>
        </div>
      </nav>
    )
  },
)

/**
 * MainNavigation is a controlled component that provides a structured navigation
 * layout with customizable slots for `Account`, `Menu`, and `Bottom`. The navigation
 * state `expanded` is controlled via the expanded prop
 * and can be toggled programmatically or through a callback function passed to the
 * `onClick` prop.
 *
 * [Figma](https://www.figma.com/design/Xf38PdCKeISWnBoh0pLFIIWf/%F0%9F%A7%B1-MC-Desktop-Components?node-id=30984-6866&node-type=instance&m=dev)
 * */
export const MainNavigation = Object.assign(MainNavigationComponent, {
  Item: MainNavigationItem,
})
